import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "../../services/authService";
import { tokenManager } from "../../utils/tokenManager";
import { fetchAccountAccess } from "../../api/accountAccess";
import {
  clearAppRole,
  loadAccountAccess,
  persistAccountAccess,
  syncAppRoleFromAccess,
  syncAppRoleFromUser,
} from "../../utils/appRole";
import {
  shouldEndAuthSession,
  messageForAuthCode,
  messageForLoginFailure,
  endAuthSession,
  clearLoginRedirectReason,
} from "../../utils/authSessionFailure.js";

function isAuthSessionError(error) {
  if (!error || typeof error !== "object") return false;
  const status = Number(error.status) || 0;
  const code = typeof error.code === "string" ? error.code : "";
  return shouldEndAuthSession(status, code);
}

/**
 * Helper function to set cookie
 */
// const setCookie = (name, value) => {
//   document.cookie = `${name}=${value};path=/;SameSite=Lax`;
// };

/**
 * Helper function to clear cookie
 */
// const clearCookie = (name) => {
//   document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
// };

// ======================
// 🔹 SEND OTP
// ======================
export const sendOtp = createAsyncThunk(
  "auth/sendOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await authService.sendOtp({ email });
      return response;
    } catch (error) {
      console.error("Send OTP failed:", error.message);
      return rejectWithValue(error.message || "Send OTP failed");
    }
  }
);

// ======================
// 🔹 VERIFY OTP
// ======================
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await authService.verifyOtp({ email, otp });
      return response;
    } catch (error) {
      console.error("Verify OTP failed:", error.message);
      return rejectWithValue(error.message || "Verify OTP failed");
    }
  }
);
// ======================
// 🔹 RESET PASSWORD
// ======================
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (
    { email, newPassword, confirmNewPassword, passwordResetToken, otp },
    { rejectWithValue }
  ) => {
    try {
      const response = await authService.resetPassword({
        email,
        newPassword,
        confirmNewPassword,
        passwordResetToken,
        otp,
      });

      return response;
    } catch (error) {
      console.error("Reset Password failed:", error.message);
      return rejectWithValue(error.message || "Reset Password failed");
    }
  }
);

/**
 * Async thunk for user login
 */
export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await authService.login(email, password);

      // Profile in localStorage; token memory-only (httpOnly cookie from Node).
      tokenManager.setToken(response.token, response.user);

      console.log("Login successful:", {
        hasToken: !!response.token,
        cookieAuth: !!response.cookieAuth,
        user: response.user?.email,
      });

      let accountAccess = null;
      try {
        accountAccess = await fetchAccountAccess();
        persistAccountAccess(accountAccess);
        syncAppRoleFromAccess(accountAccess);
      } catch (accessErr) {
        if (isAuthSessionError(accessErr)) {
          tokenManager.clearAuth();
          clearLoginRedirectReason();
          const loginMsg = messageForLoginFailure(accessErr.code);
          return rejectWithValue(
            loginMsg || accessErr.message || "Login failed",
          );
        }
        console.warn("Account access fetch failed:", accessErr.message);
        syncAppRoleFromUser(response.user);
      }

      return { ...response, accountAccess };
    } catch (error) {
      console.error("Login failed:", error.message);
      const code = error?.code;
      let loginMsg = messageForLoginFailure(code);
      if (!loginMsg) {
        const lower = String(error.message || "").toLowerCase();
        if (lower.includes("role is inactive") || lower.includes("assigned role is inactive")) {
          loginMsg = messageForLoginFailure("ROLE_INACTIVE");
        } else if (
          lower.includes("account is inactive") ||
          lower.includes("user account is inactive")
        ) {
          loginMsg = messageForLoginFailure("ACCOUNT_INACTIVE");
        }
      }
      if (loginMsg) {
        clearLoginRedirectReason();
        return rejectWithValue(loginMsg);
      }
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

export const refreshUser = createAsyncThunk(
  "auth/refreshUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const token = tokenManager.getToken();
      const response = await authService.refreshUser(token);

      tokenManager.setToken(response.token, response.user);

      return response;
    } catch (error) {
      console.error("Login failed:", error.message);
      return rejectWithValue(error.message || "Login failed");
    }
  }
);

/**
 * Async thunk for user logout
 */
export const logoutUser = createAsyncThunk(
  "auth/logout",
  async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.warn("Logout API call failed; clearing local session:", error.message);
    }
    tokenManager.clearAuth();
    clearAppRole();
    return {};
  },
);

/**
 * Async thunk for validating existing token
 */
export const validateToken = createAsyncThunk(
  "auth/validateToken",
  async (_, { rejectWithValue }) => {
    // Cookie is authoritative after reload (no JS token). User profile is bootstrap only.
    const user = tokenManager.getUser();
    if (!user) {
      return rejectWithValue("No valid session found");
    }

    let accountAccess = null;
    try {
      accountAccess = await fetchAccountAccess();
      persistAccountAccess(accountAccess);
      syncAppRoleFromAccess(accountAccess);
    } catch (accessErr) {
      if (isAuthSessionError(accessErr)) {
        const msg = messageForAuthCode(accessErr.code, accessErr.message);
        endAuthSession(msg);
        return rejectWithValue(msg);
      }
      console.warn("Account access fetch failed:", accessErr.message);
      syncAppRoleFromUser(user);
    }

    return {
      token: tokenManager.getToken(),
      user: tokenManager.getUser() || user,
      accountAccess,
    };
  }
);

export const refreshAccountAccess = createAsyncThunk(
  "auth/refreshAccountAccess",
  async (_, { rejectWithValue }) => {
    if (!tokenManager.isAuthenticated()) {
      return rejectWithValue("Not authenticated");
    }
    try {
      const accountAccess = await fetchAccountAccess();
      persistAccountAccess(accountAccess);
      syncAppRoleFromAccess(accountAccess);
      return accountAccess;
    } catch (error) {
      if (isAuthSessionError(error)) {
        const msg = messageForAuthCode(error.code, error.message);
        endAuthSession(msg);
        return rejectWithValue(msg);
      }
      return rejectWithValue(error.message || "Failed to load access");
    }
  },
);

/**
 * Get initial auth state from localStorage
 */
const getInitialAuthState = () => {
  const token = tokenManager.getToken();
  const user = tokenManager.getUser();
  const isAuthenticated =
    tokenManager.isAuthenticated() && tokenManager.isTokenValid();

  const cachedAccess = loadAccountAccess();
  if (cachedAccess) {
    syncAppRoleFromAccess(cachedAccess);
  } else if (user) {
    syncAppRoleFromUser(user);
  }

  console.log("Initial auth state:", {
    token: !!token,
    user: !!user,
    isAuthenticated,
  });

  return {
    user,
    token,
    isAuthenticated,
    accountAccess: cachedAccess,
    /** Refetched on every page load when authenticated (see App refreshAccountAccess). */
    accessLoaded: false,
    loading: false,
    otpLoading: false,
    error: null,
  };
};

/**
 * Initial state for auth slice
 */
const initialState = getInitialAuthState();

/**
 * Auth slice with reducers for authentication state management
 */
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.accountAccess = null;
      state.accessLoaded = false;
      state.loading = false;
      state.error = null;
      tokenManager.clearAuth();
      clearAppRole();
      // Clear token cookie
      // clearCookie("tkn");
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.accountAccess = action.payload.accountAccess ?? null;
        state.accessLoaded = true;
        state.error = null;
        console.log("Login successful, token and user stored");
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      // Logout cases — do not set `loading` (avoids full-app spinner blocking redirect)
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.accountAccess = null;
        state.accessLoaded = false;
        state.error = null;
        clearAppRole();
      })
      .addCase(logoutUser.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.accountAccess = null;
        state.accessLoaded = false;
        tokenManager.clearAuth();
        clearAppRole();
      })
      // Token validation cases
      .addCase(validateToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.accountAccess = action.payload.accountAccess ?? null;
        state.accessLoaded = true;
        state.error = null;
        console.log("Token validation successful");
      })
      .addCase(refreshAccountAccess.fulfilled, (state, action) => {
        state.accountAccess = action.payload;
        state.accessLoaded = true;
      })
      .addCase(refreshAccountAccess.rejected, (state, action) => {
        if (!tokenManager.isAuthenticated()) {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          state.accountAccess = null;
          state.accessLoaded = false;
          state.error = action.payload;
          return;
        }
        if (!state.accountAccess) {
          const cached = loadAccountAccess();
          if (cached) {
            state.accountAccess = cached;
            syncAppRoleFromAccess(cached);
          }
        }
        state.accessLoaded = true;
      })
      .addCase(validateToken.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload;
        console.log("Token validation failed:", action.payload);
      })
      // --- Send OTP ---
      .addCase(sendOtp.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state) => {
        state.otpLoading = false;
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload;
      })

      // --- Verify OTP ---
      .addCase(verifyOtp.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.otpLoading = false;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload;
      })
      // --- Reset Password ---
      .addCase(resetPassword.pending, (state) => {
        state.otpLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.otpLoading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, resetAuth } = authSlice.actions;

export const selectAccountAccess = (state) => state.auth.accountAccess;
export const selectAccessLoaded = (state) => state.auth.accessLoaded;
export default authSlice.reducer;
